// Detective control: scan already-posted journals for likely duplicate bank
// transactions (same date + amount + description) and let the user reverse the
// extra copies. Append-only — "removing" a duplicate posts an offsetting entry.
import React, { useState, useMemo } from 'react';
import { reverseJournal } from './firebase';
import { fmtMoney } from './insights';

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

function scan(journals) {
  const reversedIds = new Set((journals || []).filter((j) => j.reverses).map((j) => j.reverses));
  const active = (journals || []).filter((j) => !j.reverses && !reversedIds.has(j.id));
  const groups = {};
  active.forEach((j) => {
    const amt = j.source && typeof j.source.amount === 'number' ? j.source.amount : null;
    if (amt == null) return; // only fingerprintable bank-import journals
    const desc = j.source && j.source.description != null ? j.source.description : j.narration;
    const key = String(j.date || '') + '|' + (Math.round(amt * 100) / 100) + '|' + norm(desc);
    (groups[key] = groups[key] || { date: j.date, amount: amt, desc, items: [] }).items.push(j);
  });
  return Object.values(groups).filter((g) => g.items.length > 1).sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
}

export function DuplicateScan({ project, journals, coa, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const acctName = (code) => (coa.find((a) => a.code === code) || {}).name || code;
  const groups = useMemo(() => scan(journals), [journals]);
  const extraCount = groups.reduce((s, g) => s + (g.items.length - 1), 0);

  async function rev(j) {
    if (!window.confirm('Reverse this entry as a duplicate? It posts an offsetting entry (kept in the audit trail) and is itself undoable.')) return;
    setBusy(j.id);
    try { await reverseJournal(project.id, j.id, 'Duplicate removed'); if (onRefresh) await onRefresh(); }
    catch (e) { alert('Reverse failed: ' + e.message); }
    setBusy(null);
  }

  return (
    <div style={{ maxWidth: 900, marginTop: 28 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>⧉ Duplicate scan</div>
      <div style={{ fontSize: 12, color: '#9a9488', marginTop: 2, marginBottom: 12 }}>
        Finds posted bank transactions that share the same date, amount and description — usually from overlapping or re-uploaded statements. Keep one of each and reverse the extras.
      </div>

      {groups.length === 0 ? (
        <div style={{ background: '#eef5ef', border: '1px solid #cfe3d3', borderRadius: 8, padding: 14, fontSize: 13, color: '#3d7a52' }}>✓ No duplicate transactions found.</div>
      ) : (
        <>
          <div style={{ background: '#fff7e6', border: '1px solid #f0d9a8', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#8a6d1d', marginBottom: 12 }}>
            ⚠ {groups.length} transaction{groups.length > 1 ? 's appear' : ' appears'} more than once — <strong>{extraCount}</strong> extra cop{extraCount > 1 ? 'ies' : 'y'} to review. Reverse the duplicates (keep one of each).
          </div>
          {groups.map((g, gi) => (
            <div key={gi} style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{g.date} · {g.desc || '(no description)'}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: g.amount < 0 ? '#b3623d' : '#3d7a52' }}>{g.amount < 0 ? '−' : '+'}{fmtMoney(Math.abs(g.amount)).replace('R ', 'R ')} · {g.items.length} copies</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {g.items.map((j, i) => {
                    const contra = (j.lines || []).filter((l) => !(l.accountCode || '').startsWith('1000') && (Number(l.dr) || 0) + (Number(l.cr) || 0) > 0).map((l) => acctName(l.accountCode)).join(', ');
                    return (
                      <tr key={j.id} style={{ borderTop: '1px solid #f4f2ed' }}>
                        <td style={{ padding: '6px 8px', fontSize: 11, color: '#9a9488', width: 24 }}>{i === 0 ? '①' : '⊕'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{j.narration}</td>
                        <td style={{ padding: '6px 8px', fontSize: 11, color: '#9a9488' }}>{contra || '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 10, color: '#9a9488' }}>{(j.source && j.source.file) || ''}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                          {i === 0
                            ? <span style={{ fontSize: 10, color: '#3d7a52', fontWeight: 600 }}>keep</span>
                            : <button disabled={busy === j.id} onClick={() => rev(j)} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid #e8cccc', background: '#fff', color: '#b33', fontWeight: 600, cursor: 'pointer' }}>{busy === j.id ? '…' : 'Reverse duplicate'}</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
